import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from '../../../../services/app-config.service';
import { IAppConfig } from '../../../../shared/interfaces/app-config';
import { ISedmsFormOutput } from 'src/app/shared/interfaces/form/sedms-form-output.model';
import { handleError } from 'src/app/shared/functions/error-handler';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { Evaluation } from '../interfaces/evaluation-checklist';
import { Compliance } from 'src/app/modules/iep/iep-document/interfaces/compliance';
import {
  ReasonForDelayRequest,
  ReferenceValueResponse,
} from '../../interfaces/reason-for-delay';

@Injectable({
  providedIn: 'root',
})
export class AssessmentService<T extends ISedmsFormOutput> {
  config: IAppConfig;

  private assessmentDates: BehaviorSubject<Date[] | null> = new BehaviorSubject<
    Date[] | null
  >(null);
  assessmentDates$: Observable<Date[]> = this.assessmentDates.asObservable();

  private validLanguageData = new BehaviorSubject<any | null>(null);
  validLanguageData$ = this.validLanguageData.asObservable();

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public submit(request: T, type: string): Observable<any> {
    return this.http
      .put(`${this.config.apiUrl}/Evaluations`, request)
      .pipe(catchError(handleError));
  }

  public get(id: string, type: string): Observable<any> {
    return this.http.get<any[]>(`${this.config.apiUrl}/Evaluations/` + id).pipe(
      map((res: any) => (!res ? {} : res)),
      catchError(handleError)
    );
  }

  public getOccupationalTeacherChecklist(
    id: string,
    evaluationType: number
  ): Observable<ApiResponse<Evaluation>> {
    return this.http
      .get<ApiResponse<Evaluation>>(
        `${this.config.apiUrl}/EvaluationChecklist/Get/${id}/${evaluationType}`
      )
      .pipe(catchError(handleError));
  }

  public getTeacherChecklist(
    id: string,
    evaluationType: number
  ): Observable<ApiResponse<Evaluation>> {
    return this.http
      .get<ApiResponse<Evaluation>>(
        `${this.config.apiUrl}/EvaluationChecklist/Get/${id}/${evaluationType}`
      )
      .pipe(
        map((res: ApiResponse<Evaluation>) =>
          !res
            ? ({
                isRequestSucessful: false,
                value: null,
              } as ApiResponse<Evaluation>)
            : res
        ),
        catchError(handleError)
      );
  }

  public submitPhysicalTeacherChecklist(
    request: Evaluation
  ): Observable<ApiResponse<string>> {
    return this.http
      .post<ApiResponse<string>>(
        `${this.config.apiUrl}/EvaluationChecklist/Add`,
        request
      )
      .pipe(catchError(handleError));
  }

  public Docsubmit(
    nycId: string,
    evaluationSk: string,
    evaluationType: number
  ): Observable<Uint8Array> {
    return this.http
      .get<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/EvalDocument?nycId=${nycId}&evaluationSk=${evaluationSk}&EvaluationType=${evaluationType}`
      )
      .pipe(catchError(handleError));
  }

  public updateSTACStatus(
    evaluationSk: string,
    isSubmit: boolean
  ): Observable<boolean> {
    return this.http
      .get<boolean>(
        `${this.config.apiUrl}/Evaluations/UpdateSTACStatus?evaluationSk=${evaluationSk}&isSubmit=${isSubmit}`
      )
      .pipe(catchError(handleError));
  }

  public getComplianceDate(id: string): Observable<Compliance> {
    return this.http
      .get<Compliance>(
        `${this.config.apiUrl}/Evaluations/GetComplianceDate?evaluationSk=${id}`
      )
      .pipe(catchError(handleError));
  }

  public getReasonDelay(
    id: string,
    isEval: boolean
  ): Observable<ReferenceValueResponse[]> {
    return this.http
      .get<ReferenceValueResponse[]>(
        `${this.config.apiUrl}/Evaluations/GetReasonforDelay?caseActionsk=${id}&isEval=${isEval}`
      )
      .pipe(catchError(handleError));
  }

  public submitReasonForDelay(
    evalutionSk: string,
    request: ReasonForDelayRequest
  ): Observable<number> {
    return this.http
      .post<number>(
        `${this.config.apiUrl}/Evaluations/SaveReasonforDelay?evaluationSk=${evalutionSk}`,
        request
      )
      .pipe(catchError(handleError));
  }

  // TODO: Not sure what the type should be, looks like the backend returns a `string` but the UI is expecting an object
  public getPreviousReport(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.config.apiUrl}/Evaluations/physical/${id}`)
      .pipe(catchError(handleError));
  }

  getAssessmentDates(data: Date[]): void {
    this.assessmentDates.next(data);
  }

  getLanguageValidation(data): void {
    this.validLanguageData.next(data);
  }
}
